import {
  Card,
  Text,
  Stack,
  Avatar,
  Flex,
  Box,
  ActionIcon,
  Badge,
  Grid,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useHover } from "@mantine/hooks"
import { IconPlus, IconSearch } from "@tabler/icons-react"
import _ from "lodash"
import { useMemo, useCallback } from "react"

import { PeriodTypeEnum } from "@costory/types/endpoints/billy"
import { Filters } from "@costory/types/filters"
import { ExplorerChartType } from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"
import { getBadgeColor, getBadgeLabel } from "@costory/shared/utils/digest"
import { calculateRelativeChange } from "@costory/shared/utils/format"

import { AreaChartWithReference } from "@costory/front/components/charts/AreaChartWithReference"
import { WaterfallChart } from "@costory/front/components/charts/WaterfallChart"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  COST_CARD_HEIGHT,
  WATERFALL_CHART_LIMIT,
} from "@costory/front/pages/DigestV2/components/SuggestedCards/constants"
import { useChartActions } from "@costory/front/pages/DigestV2/components/SuggestedCards/useChartActions"
import { PageType } from "@costory/front/pages/DigestV2/components/types"
import { useExplorerChangeDataPointQuery } from "@costory/front/queries/explore"

type CostCardProps = {
  cost: number
  previousCost: number
  showSparkline?: boolean
  filters?: Filters
  form: UseFormReturnType<Filters>
  setChartType: (type: ExplorerChartType) => void
  setPage: (page: PageType) => void
  initialFormValues: Filters
  title?: string
  isWaterfallChart?: boolean
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  editContent: string
  isNotOtherNode?: boolean
  setLoading?: (loading: boolean) => void
}

export function CostCard({
  cost,
  previousCost,
  filters,
  showSparkline = false,
  form,
  setChartType,
  setPage,
  initialFormValues,
  title,
  isWaterfallChart = false,
  setEditContent,
  editContent,
  isNotOtherNode = true,
  setLoading,
}: CostCardProps) {
  const { relativeChange, formattedDiff, color, icon } = useMemo(() => {
    const diff = cost - previousCost
    const result = calculateRelativeChange(diff, previousCost, cost)
    return {
      relativeChange: result.relativeChange,
      formattedDiff: result.formattedDiff,
      color: result.color,
      icon: result.icon,
    }
  }, [cost, previousCost])

  const changeDataPointQuery = useExplorerChangeDataPointQuery(
    !isWaterfallChart ? { ...filters!, groupBy: "" } : undefined,
    true,
  )

  const {
    addExplorerImageToNote,
    createChartUrl,
    isChartAdded,
    handleQuickAdd,
    handleAnalyze,
  } = useChartActions({
    form,
    setChartType,
    setPage,
    setEditContent,
    editContent,
    setLoading,
  })

  const chartUrl = useMemo(() => {
    const chartFilters = isWaterfallChart
      ? { ...initialFormValues, limit: WATERFALL_CHART_LIMIT }
      : { ...filters!, groupBy: "" }
    const chartType = isWaterfallChart
      ? ExplorerChartType.WATERFALL
      : ExplorerChartType.LINE
    return createChartUrl(chartFilters, chartType)
  }, [isWaterfallChart, initialFormValues, filters, createChartUrl])

  const detectedPatterns = useMemo(() => {
    if (
      isWaterfallChart ||
      !changeDataPointQuery.data?.changeDataPointPeriods
    ) {
      return []
    }

    const uniquePatternTypes = new Set(
      changeDataPointQuery.data.changeDataPointPeriods
        .filter(
          (period) =>
            period.period_type &&
            period.period_type !== PeriodTypeEnum.NO_TREND,
        )
        .map((period) => period.period_type),
    )

    return Array.from(uniquePatternTypes).map((periodType) => ({
      type: periodType,
      color: getBadgeColor(periodType),
      label: getBadgeLabel(periodType),
    }))
  }, [isWaterfallChart, changeDataPointQuery.data?.changeDataPointPeriods])

  const handleTrendAnalysis = useCallback(() => {
    const trendFilters = { ...filters!, groupBy: "" }
    handleAnalyze(trendFilters, ExplorerChartType.LINE)
  }, [handleAnalyze, filters])

  const handleContributionAnalysis = useCallback(() => {
    handleAnalyze(initialFormValues, ExplorerChartType.WATERFALL)
  }, [handleAnalyze, initialFormValues])

  const handleClick = useMemo(
    () => (isWaterfallChart ? handleContributionAnalysis : handleTrendAnalysis),
    [isWaterfallChart, handleContributionAnalysis, handleTrendAnalysis],
  )

  const handleQuickAddClick = useCallback(
    async (e: React.MouseEvent) => {
      const chartFilters = isWaterfallChart
        ? { ...initialFormValues, limit: WATERFALL_CHART_LIMIT }
        : { ...filters!, groupBy: "" }
      const chartType = isWaterfallChart
        ? ExplorerChartType.WATERFALL
        : ExplorerChartType.LINE
      await handleQuickAdd(e, chartFilters, chartType)
    },
    [isWaterfallChart, initialFormValues, filters, handleQuickAdd],
  )
  const { ref, hovered } = useHover()
  return (
    <Card
      ref={ref}
      withBorder={false}
      radius="md"
      p={5}
      h="100%"
      style={{ display: "flex", flexDirection: "column", cursor: "pointer" }}
      onClick={handleClick}
    >
      <Stack gap={4} style={{ flexGrow: 1 }}>
        <Grid align="center" gutter="xs">
          <Grid.Col span="auto">
            <Flex align="center" gap="xs" wrap="wrap">
              <Text size="md" fw={600} style={{ minWidth: 0, flex: 1 }}>
                {title}
              </Text>
              {relativeChange !== "0%" && (
                <Flex align="center" gap={4} wrap="wrap">
                  <Avatar radius="xl" size={16} color={color} variant="light">
                    {icon}
                  </Avatar>
                  <Text
                    size="sm"
                    fw={500}
                    c={color}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {formattedDiff}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Grid.Col>
          <Grid.Col span="content">
            <Flex gap={4}>
              <ActionIcon
                size={20}
                variant="light"
                color="gray"
                onClick={handleQuickAddClick}
                style={{ flexShrink: 0 }}
                loading={addExplorerImageToNote.isPending}
                disabled={!isNotOtherNode || isChartAdded(chartUrl)}
                opacity={!isNotOtherNode || isChartAdded(chartUrl) ? 0.5 : 1}
                data-intercom-target="costory-tour-digest-card-quick-add"
              >
                <IconPlus size={11} />
              </ActionIcon>
              <ActionIcon
                size={20}
                variant="light"
                color="gray"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClick()
                }}
                data-intercom-target="costory-tour-digest-card-analyze"
              >
                <IconSearch size={11} />
              </ActionIcon>
            </Flex>
          </Grid.Col>
        </Grid>
        {!isWaterfallChart && (
          <Flex gap="xs" wrap="wrap" align="center">
            {detectedPatterns.length > 0 && (
              <>
                {detectedPatterns.slice(0, 2).map((pattern, index) => (
                  <Badge
                    key={`${pattern.type}-${index}`}
                    size="xs"
                    color={pattern.color}
                    variant="light"
                    data-intercom-target="costory-tour-digest-cost-card-pattern-badge"
                  >
                    {pattern.label}
                  </Badge>
                ))}
                {detectedPatterns.length > 2 && (
                  <Badge size="xs" color="gray" variant="light">
                    +{detectedPatterns.length - 2}
                  </Badge>
                )}
                <Text size="xs" c="dimmed" fw={500}>
                  Detected
                </Text>
              </>
            )}
          </Flex>
        )}
      </Stack>
      {hovered && (
        <Box
          style={{
            position: "absolute",
            top: "30%",
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            borderRadius: "md",
          }}
        >
          <Text c="textSecondary" fw={700}>
            Click to Explore
          </Text>
        </Box>
      )}
      {isWaterfallChart && (
        <Box pb={1} style={{ flexGrow: 1 }}>
          <WaterfallChart
            filters={{
              ...initialFormValues,
              limit: WATERFALL_CHART_LIMIT,
            }}
            withTooltip={false}
            height={COST_CARD_HEIGHT}
            isCostCard
          />
        </Box>
      )}
      {showSparkline && !isWaterfallChart && (
        <QueryWrapper query={changeDataPointQuery}>
          {({ data }) => {
            const sortedTotalsData = _.orderBy(
              data.totals,
              (item) => dayjs(item.agg_date).toDate(),
              "asc",
            )
            return (
              <Box
                style={{ flexGrow: 1, minHeight: "80px" }}
                onClick={handleClick}
              >
                <AreaChartWithReference
                  data={sortedTotalsData}
                  series={[{ name: "cost", color }]}
                  xAxisProps={{
                    ticks: [
                      sortedTotalsData[0]?.agg_date,
                      sortedTotalsData[sortedTotalsData.length - 1]?.agg_date,
                    ],
                    tickFormatter: (value: string) =>
                      dayjs(value).format(DEFAULT_DATE_FORMAT),
                    fontSize: 12,
                  }}
                  changeDataPointPeriods={
                    changeDataPointQuery.data?.changeDataPointPeriods
                  }
                  showTrends={true}
                  currency={filters?.currency}
                  onClick={handleClick}
                />
              </Box>
            )
          }}
        </QueryWrapper>
      )}
    </Card>
  )
}
